(() => {
  if (customElements.get('yg-collection-bar')) return;

  class CollectionPhoneFinder extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.brandValue = this.querySelector('[data-collection-phone-finder-brand-value]');
      this.modelValue = this.querySelector('[data-collection-phone-finder-model-value]');
      this.brandIcon = this.querySelector('[data-collection-phone-finder-brand-icon]');
      this.triggers = Array.from(this.querySelectorAll('[data-collection-phone-finder-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-collection-phone-finder-panel]'));
      this.brandOptions = Array.from(this.querySelectorAll('[data-collection-phone-finder-brand]'));
      this.modelGroups = Array.from(this.querySelectorAll('[data-collection-phone-finder-model-group]'));
      this.sheetPanels = Array.from(this.querySelectorAll('[data-collection-phone-finder-sheet-panel]'));
      this.sheet = this.querySelector('[data-collection-phone-finder-sheet]');
      this.dialog = this.querySelector('[data-collection-phone-finder-dialog]');
      this.selectedBrand = this.dataset.initialBrand || '';
      this.openTrigger = null;
      this.sheetTrigger = null;
      this.scrollState = null;

      this.triggers.forEach((trigger) => {
        trigger.dataset.desktopControls = trigger.getAttribute('aria-controls') || '';
      });

      this.addEventListener('click', this.handleClick.bind(this), { signal: this.signal });
      this.addEventListener('keydown', this.handleComponentKeydown.bind(this), { signal: this.signal });
      document.addEventListener('pointerdown', this.handleDocumentPointerdown.bind(this), { signal: this.signal });
      document.addEventListener('keydown', this.handleDocumentKeydown.bind(this), { signal: this.signal });
      this.mobileMedia.addEventListener('change', this.handleViewportChange.bind(this), { signal: this.signal });

      this.querySelectorAll('img').forEach((image) => {
        image.addEventListener(
          'error',
          () => {
            image.hidden = true;
          },
          { signal: this.signal },
        );
      });
    }

    trackFinderInteraction(target, interactionName, destinationUrl) {
      if (!target?.dataset?.analyticsElementId) return;

      const details = {
        componentId: 'collection_device_finder',
        elementType: target.dataset.analyticsElementType || 'button',
        elementId: target.dataset.analyticsElementId,
        interactionName,
        interactionResult: 'success',
      };

      if (destinationUrl) {
        try {
          details.destinationType = 'collection';
          details.destinationPath = new URL(destinationUrl, document.baseURI).pathname;
        } catch (error) {}
      }

      window.TorrasAnalytics?.trackInteraction(target, details);
    }

    trackSheetLifecycle(interactionName, elementId, interactionResult) {
      if (!this.sheet || !interactionName || !elementId) return;

      window.TorrasAnalytics?.trackInteraction(this.sheet, {
        componentId: 'collection_device_finder',
        elementType: 'component',
        elementId,
        interactionName,
        interactionResult,
      });
    }

    disconnectedCallback() {
      if (!this.abortController) return;
      this.abortController.abort();
      this.abortController = null;
      this.closeSheet(false);
      this.unlockScroll();
    }

    handleClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;

      if (target.matches('[data-collection-phone-finder-close]')) {
        event.preventDefault();
        this.closeSheet(true, target.dataset.analyticsElementId || 'close_button', 'cancel');
        return;
      }

      if (target.matches('[data-collection-phone-finder-brand]')) {
        event.preventDefault();
        const brandHandle = target.dataset.collectionPhoneFinderBrand;
        this.trackFinderInteraction(target, 'select_brand');
        this.selectBrand(brandHandle);
        if (!this.mobileMedia.matches) {
          this.closePanels(false);
          this.getTrigger('brand')?.focus();
        }
        return;
      }

      if (target.matches('[data-collection-phone-finder-trigger]')) {
        event.preventDefault();
        if (this.mobileMedia.matches) {
          this.openSheet(target);
        } else {
          this.togglePanel(target.dataset.collectionPhoneFinderTrigger, target);
        }
        return;
      }

      if (target.matches('[data-collection-phone-finder-model-link]')) {
        this.trackFinderInteraction(target, 'select_model', target.getAttribute('href'));
        this.closePanels(false);
        if (this.mobileMedia.matches) this.closeSheet(false, 'action_navigation', 'success');
      }
    }

    handleComponentKeydown(event) {
      const tab = event.target.closest('[role="tab"][data-collection-phone-finder-brand]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const tabs = this.brandOptions.filter((option) => option.getAttribute('role') === 'tab');
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex < 0) return;

      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      const nextTab = tabs[nextIndex];
      this.trackFinderInteraction(nextTab, 'select_brand');
      this.selectBrand(nextTab.dataset.collectionPhoneFinderBrand);
      nextTab.focus();
    }

    handleDocumentPointerdown(event) {
      if (!this.contains(event.target)) this.closePanels(false);
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        if (this.sheet && !this.sheet.hidden) {
          event.preventDefault();
          this.closeSheet(true, 'escape_key', 'cancel');
        } else if (this.panels.some((panel) => !panel.hidden)) {
          event.preventDefault();
          this.closePanels(true);
        }
        return;
      }

      if (event.key === 'Tab' && this.sheet && !this.sheet.hidden) {
        this.trapSheetFocus(event);
      }
    }

    handleViewportChange() {
      this.closePanels(false);
      this.closeSheet(false);
    }

    getTrigger(type) {
      return this.triggers.find((trigger) => trigger.dataset.collectionPhoneFinderTrigger === type);
    }

    getPanel(type) {
      return this.panels.find((panel) => panel.dataset.collectionPhoneFinderPanel === type);
    }

    togglePanel(type, trigger) {
      const panel = this.getPanel(type);
      if (!panel) return;
      const shouldOpen = panel.hidden;
      this.closePanels(false);
      if (!shouldOpen) return;

      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.openTrigger = trigger;
    }

    closePanels(restoreFocus) {
      const triggerToRestore = this.openTrigger;
      this.panels.forEach((panel) => {
        panel.hidden = true;
      });
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.openTrigger = null;
      if (restoreFocus && triggerToRestore?.isConnected) triggerToRestore.focus();
    }

    selectBrand(handle) {
      const matchingOptions = this.brandOptions.filter(
        (option) => option.dataset.collectionPhoneFinderBrand === handle,
      );
      const sourceOption = matchingOptions[0];
      if (!sourceOption) return;

      this.selectedBrand = handle;
      this.dataset.initialBrand = handle;

      this.brandOptions.forEach((option) => {
        const isActive = option.dataset.collectionPhoneFinderBrand === handle;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', String(isActive));
        if (option.getAttribute('role') === 'tab') option.tabIndex = isActive ? 0 : -1;
      });

      this.modelGroups.forEach((group) => {
        group.hidden = group.dataset.collectionPhoneFinderModelGroup !== handle;
      });
      this.sheetPanels.forEach((panel) => {
        panel.hidden = panel.dataset.collectionPhoneFinderSheetPanel !== handle;
      });

      const brandLabel = sourceOption.dataset.brandLabel || '';
      const brandIcon = sourceOption.dataset.brandIcon || '';
      const firstModel = sourceOption.dataset.firstModel || '';
      if (this.brandValue) this.brandValue.textContent = brandLabel;
      if (this.modelValue) this.modelValue.textContent = firstModel;
      if (this.brandIcon) {
        this.brandIcon.hidden = !brandIcon;
        if (brandIcon) this.brandIcon.src = brandIcon;
      }

      this.querySelectorAll('[data-collection-phone-finder-model-link]').forEach((link) => {
        const group = link.closest(
          '[data-collection-phone-finder-model-group], [data-collection-phone-finder-sheet-panel]',
        );
        const groupHandle =
          group?.dataset.collectionPhoneFinderModelGroup || group?.dataset.collectionPhoneFinderSheetPanel || '';
        const isSelected = groupHandle === handle && link.dataset.modelTitle === firstModel;
        link.classList.toggle('is-active', isSelected);
        if (link.getAttribute('role') === 'option') link.setAttribute('aria-selected', String(isSelected));
      });
    }

    openSheet(trigger) {
      if (!this.sheet || !this.dialog) return;
      const wasOpen = !this.sheet.hidden;
      this.closePanels(false);
      this.sheetTrigger = trigger;
      this.sheet.hidden = false;
      this.sheet.setAttribute('aria-hidden', 'false');
      this.triggers.forEach((item) => {
        item.setAttribute('aria-expanded', String(item === trigger));
        item.setAttribute('aria-controls', this.sheet.id);
      });
      this.lockScroll();

      if (!wasOpen) {
        this.trackSheetLifecycle(
          'open',
          trigger?.dataset?.analyticsElementId || 'sheet_trigger',
          'success',
        );
      }

      window.requestAnimationFrame(() => {
        this.sheet.classList.add('is-open');
        window.requestAnimationFrame(() => {
          if (this.sheet.hidden) return;
          const activeTab = this.sheet.querySelector('[role="tab"][aria-selected="true"]');
          (activeTab || this.dialog).focus({ preventScroll: true });
        });
      });
    }

    closeSheet(restoreFocus, analyticsElementId = 'programmatic_close', analyticsResult = 'cancel') {
      if (!this.sheet) return;
      const wasOpen = !this.sheet.hidden;
      const triggerToRestore = this.sheetTrigger;
      this.sheet.classList.remove('is-open');
      this.sheet.hidden = true;
      this.sheet.setAttribute('aria-hidden', 'true');
      this.triggers?.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', trigger.dataset.desktopControls || '');
      });
      this.sheetTrigger = null;
      this.unlockScroll();
      if (wasOpen) this.trackSheetLifecycle('close', analyticsElementId, analyticsResult);
      if (restoreFocus && triggerToRestore?.isConnected) triggerToRestore.focus();
    }

    trapSheetFocus(event) {
      if (!this.dialog) return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      if (this.scrollState) return;
      this.scrollState = {
        bodyOverflow: document.body.style.overflow,
        documentOverflow: document.documentElement.style.overflow,
      };
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.scrollState) return;
      document.body.style.overflow = this.scrollState.bodyOverflow;
      document.documentElement.style.overflow = this.scrollState.documentOverflow;
      this.scrollState = null;
    }
  }

  customElements.define('yg-collection-bar', CollectionPhoneFinder);
})();
